import {
  CodeEditor,
  CodeFile,
  type CodeFileState,
  type KeyOverrideFunction,
  type Theme,
} from 'mini-code'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type PatternEventInfo,
  type SequenceData,
} from './bytecode.ts'
import { dspscript } from './dsp-script.ts'
import { AmplitudeWidget, NoteWidget, SliderWidget, waveformPointersMap, WaveformWidget } from './editor-widgets.ts'
import { functionDefinitions } from './function-definitions.ts'
import { readSequencerStateFromBuffer } from './lib/sequencer-state-mirror.ts'
import {
  useChannelModeStore,
  useEditorErrorsStore,
  useEditorSettingsStore,
  usePlaybackStore,
  useTabsStore,
  useUserProfileMenuStore,
} from './store.ts'
import { useTheme } from './theme.ts'
import { tokenizer } from './tokenizer.ts'
import { SequencerManager } from './ui-sequencer.ts'
import { TimelineManager } from './ui-timeline.ts'
import { errorsAreEqual } from './util.ts'
import { LevelWidget } from './widgets/level.ts'
import { TimelineWidget } from './widgets/timeline.ts'

interface EditorPanelProps {
  codeFile: CodeFile
  channel: 'A' | 'B'
  keyOverride?: KeyOverrideFunction
  onCodeChange?: (code: string) => void
  onStateChange?: (state: CodeFileState) => void
  isFullscreen?: boolean
}

export const EditorPanel = memo(
  (
    { codeFile, channel, keyOverride, onCodeChange, onStateChange, isFullscreen = false }: EditorPanelProps,
  ) => {
    const activeTabId = useTabsStore(state => state.activeTabId)
    const getTabs = useTabsStore.getState

    const prevTabIdRef = useRef<string | null>(null)
    const currentCode = codeFile.value

    // Sync widgets from store with proper memoization to avoid infinite loops
    const [widgetsState, setWidgetsState] = useState(() => {
      const state = getTabs()
      const tab = state.tabs.find(t => t.id === state.activeTabId)
      return {
        numbers: channel === 'A'
          ? (tab?.numbersWithParamsA || [])
          : (tab?.numbersWithParamsB || []),
        patterns: channel === 'A'
          ? (tab?.patternEventsA || [])
          : (tab?.patternEventsB || []),
        sequences: channel === 'A'
          ? (tab?.sequencesA || [])
          : (tab?.sequencesB || []),
        waveforms: channel === 'A'
          ? (tab?.amplitudeInfosA || [])
          : (tab?.amplitudeInfosB || []),
        waveformInfos: channel === 'A'
          ? (tab?.waveformInfosA || [])
          : (tab?.waveformInfosB || []),
        levelInfos: channel === 'A'
          ? (tab?.levelInfosA || [])
          : (tab?.levelInfosB || []),
        timelineInfos: channel === 'A'
          ? (tab?.timelineInfosA || [])
          : (tab?.timelineInfosB || []),
      }
    })

    const widgetsStateRef = useRef(widgetsState)
    widgetsStateRef.current = widgetsState

    // Subscribe to widget changes with proper equality checking
    useEffect(() => {
      const updateWidgets = (forceUpdate = false) => {
        const state = getTabs()
        const tab = state.tabs.find(t => t.id === state.activeTabId)
        const newWidgets = {
          numbers: channel === 'A'
            ? (tab?.numbersWithParamsA || [])
            : (tab?.numbersWithParamsB || []),
          patterns: channel === 'A'
            ? (tab?.patternEventsA || [])
            : (tab?.patternEventsB || []),
          sequences: channel === 'A'
            ? (tab?.sequencesA || [])
            : (tab?.sequencesB || []),
          waveforms: channel === 'A'
            ? (tab?.amplitudeInfosA || [])
            : (tab?.amplitudeInfosB || []),
          waveformInfos: channel === 'A'
            ? (tab?.waveformInfosA || [])
            : (tab?.waveformInfosB || []),
          levelInfos: channel === 'A'
            ? (tab?.levelInfosA || [])
            : (tab?.levelInfosB || []),
          timelineInfos: channel === 'A'
            ? (tab?.timelineInfosA || [])
            : (tab?.timelineInfosB || []),
        }
        // Always update if forced (tab switch) or if arrays actually changed (reference equality)
        const current = widgetsStateRef.current
        if (
          forceUpdate
          || newWidgets.numbers !== current.numbers
          || newWidgets.patterns !== current.patterns
          || newWidgets.sequences !== current.sequences
          || newWidgets.waveforms !== current.waveforms
          || newWidgets.waveformInfos !== current.waveformInfos
          || newWidgets.levelInfos !== current.levelInfos
          || newWidgets.timelineInfos !== current.timelineInfos
        ) {
          setWidgetsState(newWidgets)
        }
      }

      // Force update when effect first runs to catch any updates that happened during initialization
      updateWidgets(true)

      // Subscribe to activeTabId changes
      const unsubscribe1 = useTabsStore.subscribe(
        (state) => state.activeTabId,
        () => {
          // Force update when switching tabs
          updateWidgets(true)
        },
      )

      // Subscribe to widget arrays for the active tab only
      const unsubscribe2 = useTabsStore.subscribe(
        (state) => {
          const tab = state.tabs.find(t => t.id === state.activeTabId)
          return channel === 'A'
            ? {
              numbers: tab?.numbersWithParamsA,
              patterns: tab?.patternEventsA,
              sequences: tab?.sequencesA,
              waveforms: tab?.amplitudeInfosA,
              waveformInfos: tab?.waveformInfosA,
              levelInfos: tab?.levelInfosA,
              timelineInfos: tab?.timelineInfosA,
            }
            : {
              numbers: tab?.numbersWithParamsB,
              patterns: tab?.patternEventsB,
              sequences: tab?.sequencesB,
              waveforms: tab?.amplitudeInfosB,
              waveformInfos: tab?.waveformInfosB,
              levelInfos: tab?.levelInfosB,
              timelineInfos: tab?.timelineInfosB,
            }
        },
        () => {
          // Update widgets when arrays change (not forced, so equality check applies)
          updateWidgets(false)
        },
        {
          equalityFn: (a, b) => {
            return (
              a.numbers === b.numbers
              && a.patterns === b.patterns
              && a.sequences === b.sequences
              && a.waveforms === b.waveforms
              && a.waveformInfos === b.waveformInfos
              && a.levelInfos === b.levelInfos
              && a.timelineInfos === b.timelineInfos
            )
          },
        },
      )

      return () => {
        unsubscribe1()
        unsubscribe2()
      }
    }, [activeTabId, channel, getTabs])

    const numbersWithParams = widgetsState.numbers
    const patternEvents = widgetsState.patterns
    const sequences = widgetsState.sequences
    const amplitudeInfos = widgetsState.waveforms
    const waveformInfos = widgetsState.waveformInfos
    const levelInfos = widgetsState.levelInfos
    const timelineInfos = widgetsState.timelineInfos || []

    // Waveform buffer pointers - cached for direct WASM memory access

    // Track tab changes
    useEffect(() => {
      if (prevTabIdRef.current === null) {
        prevTabIdRef.current = activeTabId
        return
      }

      if (prevTabIdRef.current !== activeTabId) {
        prevTabIdRef.current = activeTabId
      }
    }, [activeTabId])

    const prevCodeRef = useRef(codeFile.value)
    const baseTheme = useTheme()
    const getEditorErrorsStore = useEditorErrorsStore.getState
    const hideFunctionSignatures = useEditorSettingsStore(state => state.hideFunctionSignatures)
    const wordWrap = useEditorSettingsStore(state => state.wordWrap)
    const closeMenu = useUserProfileMenuStore(state => state.closeMenu)
    const [showFlash, setShowFlash] = useState(false)
    const isPlaying = usePlaybackStore(state => state.isPlaying)
    const playingTabId = usePlaybackStore(state => state.playingTabId)
    const channelMode = useChannelModeStore(state => state.channelMode)
    const [actuallyPlaying, setActuallyPlaying] = useState(false)
    const prevActuallyPlayingRef = useRef(false)
    const waitingForCounterResetRef = useRef(false)
    const waitingStartTimeRef = useRef<number>(0)
    const lastCounterWhenWaitingRef = useRef<number>(-1)

    // Track all active notes - multiple notes can be active per sequence if they play between frames
    // Key format: `${sequenceId}-${noteIndex}` to allow multiple notes per sequence
    const activeNotesRef = useRef<Map<string, number>>(new Map()) // Map<`${sequenceId}-${noteIndex}`, samplePosition>
    const timelinePositionsRef = useRef<Map<number, any>>(new Map()) // Map<timelineId, TimelinePosition>
    const currentSampleCounterRef = useRef<number>(0)
    const sequencerManagerRef = useRef<SequencerManager | null>(null)
    const timelineManagerRef = useRef<TimelineManager | null>(null)
    const sequencerPointersRef = useRef<Map<number, number>>(new Map()) // sequenceId -> statePtr
    const fadeDuration = 200 // milliseconds
    // Convert fade duration to samples at 48kHz
    const fadeDurationSamples = Math.floor((fadeDuration / 1000) * 48000) // 9600 samples
    const patternLengthsRef = useRef<Map<number, number>>(new Map()) // Cache pattern lengths
    const sequencesRef = useRef<SequenceData[]>([]) // Store sequences for animation loop
    const getErrors = useCallback(() => {
      const store = getEditorErrorsStore()
      return channel === 'A' ? store.errorsA : store.errorsB
    }, [channel, getEditorErrorsStore])

    const initialErrors = getErrors()
    const stableErrorsRef = useRef(initialErrors)
    const [errors, setErrors] = useState(() => initialErrors)

    useEffect(() => {
      const newErrors = getErrors()
      if (!errorsAreEqual(newErrors, stableErrorsRef.current)) {
        stableErrorsRef.current = newErrors
        setErrors(newErrors)
      }
    }, [channel, getErrors])

    useEffect(() => {
      const unsubscribe = useEditorErrorsStore.subscribe(() => {
        const newErrors = getErrors()
        if (!errorsAreEqual(newErrors, stableErrorsRef.current)) {
          stableErrorsRef.current = newErrors
          setErrors(newErrors)
        }
      })
      return unsubscribe
    }, [getErrors])

    // Poll dspscript.isPlaying() to track when audio actually starts playing
    useEffect(() => {
      const interval = setInterval(() => {
        const actualPlaying = dspscript.isPlaying()
        const wasNotPlaying = !prevActuallyPlayingRef.current
        const isNowPlaying = actualPlaying

        // When playback actually starts (transitions from false to true),
        // immediately clear active notes to prevent showing old state
        // and set flag to wait for counter reset
        if (wasNotPlaying && isNowPlaying) {
          activeNotesRef.current.clear()
          waitingForCounterResetRef.current = true
          waitingStartTimeRef.current = Date.now()
          lastCounterWhenWaitingRef.current = -1
          currentSampleCounterRef.current = -1 // Reset tracking
        }

        prevActuallyPlayingRef.current = actualPlaying
        setActuallyPlaying(actualPlaying)
      }, 100)

      return () => clearInterval(interval)
    }, [])

    const theme = useMemo<Theme>(() => {
      if (!isFullscreen) return baseTheme
      return {
        ...baseTheme,
        background: 'transparent',
        gutterBackground: 'transparent',
      }
    }, [baseTheme, isFullscreen])

    useEffect(() => {
      const unsubscribe = codeFile.subscribe(() => {
        const newCode = codeFile.value
        const fullState = codeFile.getState()

        if (prevCodeRef.current !== newCode) {
          prevCodeRef.current = newCode
          onCodeChange?.(newCode)
        }

        onStateChange?.(fullState)
      })

      return unsubscribe
    }, [codeFile, onCodeChange, onStateChange])

    const handleKeyOverride: KeyOverrideFunction = useCallback(
      (event, codeFile) => {
        if (event.altKey && event.key === 'f') {
          event.preventDefault()
          const { setHideFunctionSignatures } = useEditorSettingsStore.getState()
          setHideFunctionSignatures(!hideFunctionSignatures)
          return false
        }
        if (event.altKey && event.key === 'o') {
          event.preventDefault()
          const { wordWrap: currentWordWrap, setWordWrap } = useEditorSettingsStore.getState()
          setWordWrap(!currentWordWrap)
          return false
        }
        if (event.ctrlKey && event.key === 'r') {
          return keyOverride ? keyOverride(event, codeFile) : true
        }
        if (event.ctrlKey && event.key === 'Enter') {
          event.preventDefault()
          const flashStartTime = Date.now()
          setShowFlash(true)
          window.addEventListener(
            'keyup',
            () => {
              const elapsed = Date.now() - flashStartTime
              const remaining = Math.max(0, 100 - elapsed)
              setTimeout(() => {
                setShowFlash(false)
              }, remaining)
            },
            { once: true },
          )
          if (keyOverride) {
            return keyOverride(event, codeFile)
          }
          return false
        }
        return keyOverride ? keyOverride(event, codeFile) : true
      },
      [keyOverride, codeFile, hideFunctionSignatures],
    )

    const handlePointerDown = useCallback(() => {
      closeMenu()
    }, [closeMenu])

    const containerRef = useRef<HTMLDivElement>(null)
    const codeFileRef = useRef(codeFile)
    const [, triggerRender] = useState(0)

    const dragStateRef = useRef<{
      key: string
      value: number
      min: number
      max: number
      line: number
      column: number
      length: number
      x: number
      y: number
      width: number
      isDragging: boolean
    } | null>(null)

    const rafRef = useRef<number | null>(null)
    const handlersRef = useRef<{
      move: ((e: PointerEvent) => void) | null
      up: (() => void) | null
    }>({ move: null, up: null })

    useEffect(() => {
      codeFileRef.current = codeFile
    }, [codeFile])

    useEffect(() => {
      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        if (handlersRef.current.move) {
          window.removeEventListener('pointermove', handlersRef.current.move)
          window.removeEventListener('pointerup', handlersRef.current.up!)
        }
      }
    }, [])

    useEffect(() => {
      const drag = dragStateRef.current
      if (!drag) return

      // Only clear drag state after mouse is released (handlers are null)
      // This prevents clearing while still dragging, but allows clearing after release
      if (handlersRef.current.move !== null) return

      // Wait a bit after release to ensure compilation has completed
      const timeoutId = setTimeout(() => {
        const currentDrag = dragStateRef.current
        if (!currentDrag) return
        if (handlersRef.current.move !== null) return

        const matchingInfo = numbersWithParams.find(
          info => info.line === currentDrag.line && info.column === currentDrag.column,
        )

        if (!matchingInfo) return

        // Calculate the expected string representation to compare
        // This handles rounding differences in float conversion
        const dragValueStr = currentDrag.value.toFixed(3)
        const infoValueStr = matchingInfo.value.toFixed(3)

        // Only clear if string representations match OR values are very close
        const valuesMatch = dragValueStr === infoValueStr || Math.abs(matchingInfo.value - currentDrag.value) < 0.0001

        if (valuesMatch) {
          dragStateRef.current = null
          triggerRender(n => n + 1)
        }
      }, 100) // Small delay to let compilation settle

      return () => clearTimeout(timeoutId)
    }, [numbersWithParams])

    const amplitudeWidgetCacheRef = useRef<Map<string, AmplitudeWidget>>(new Map())
    const waveformWidgetCacheRef = useRef<Map<string, WaveformWidget>>(new Map())
    const levelWidgetCacheRef = useRef<Map<string, LevelWidget>>(new Map())
    const timelineWidgetCacheRef = useRef<Map<string, TimelineWidget>>(new Map())
    const waveformPointersRef = useRef<Map<number, { bufferPtr: number; writePosition: number }>>(new Map())

    useEffect(() => {
      amplitudeWidgetCacheRef.current.clear()
      waveformWidgetCacheRef.current.clear()
      levelWidgetCacheRef.current.clear()
      timelineWidgetCacheRef.current.clear()
    }, [baseTheme, timelineInfos])

    const widgets = useMemo(() => {
      const contextId = `tab:${activeTabId}:${channel}`

      const numberWidgets = numbersWithParams.map((info) => {
        const widget = new SliderWidget(
          info,
          theme,
          codeFileRef,
          dragStateRef,
          handlersRef,
          rafRef,
          containerRef,
          triggerRender,
        )
        return widget.toEditorWidget()
      })

      const patternWidgets = patternEvents.map((event) => {
        const widget = new NoteWidget(
          event,
          activeNotesRef,
          patternLengthsRef,
        )
        const editorWidget = widget.toEditorWidget()
        return editorWidget
      })

      const nextAmplitudeCache = new Map<string, AmplitudeWidget>()
      const amplitudeWidgets = amplitudeInfos.map((info) => {
        const cacheKey = `amplitude-${contextId}-${info.id}-${info.line}-${info.column}`
        let widget = amplitudeWidgetCacheRef.current.get(cacheKey)
        if (!widget) {
          // For the main editor, only animate amplitude when editor playback is active.
          const shouldAnimate = () => {
            const { isPlaying } = usePlaybackStore.getState()
            const { playingTabId } = usePlaybackStore.getState()
            const { activeTabId: currentActiveTabId } = useTabsStore.getState()
            const { channelMode: currentChannelMode } = useChannelModeStore.getState()

            if (!isPlaying) return false

            return currentChannelMode === 'dual'
              ? (playingTabId === null)
              : (playingTabId === currentActiveTabId)
          }

          widget = new AmplitudeWidget(info, baseTheme, shouldAnimate)
          widget.setContextId(contextId)
        }
        else {
          widget.setContextId(contextId)
        }
        nextAmplitudeCache.set(cacheKey, widget)
        return widget.toEditorWidget()
      })
      amplitudeWidgetCacheRef.current = nextAmplitudeCache

      const nextWaveformCache = new Map<string, WaveformWidget>()
      const waveformWidgets = waveformInfos.map((info) => {
        const cacheKey = `waveform-${contextId}-${info.id}-${info.line}-${info.column}`
        let widget = waveformWidgetCacheRef.current.get(cacheKey)
        if (!widget) {
          const shouldAnimate = () => {
            const { isPlaying } = usePlaybackStore.getState()
            const { playingTabId } = usePlaybackStore.getState()
            const { activeTabId: currentActiveTabId } = useTabsStore.getState()
            const { channelMode: currentChannelMode } = useChannelModeStore.getState()

            if (!isPlaying) return false

            return currentChannelMode === 'dual'
              ? (playingTabId === null)
              : (playingTabId === currentActiveTabId)
          }

          widget = new WaveformWidget(info, baseTheme, shouldAnimate)
          widget.setContextId(contextId)
        }
        else {
          widget.setContextId(contextId)
        }
        nextWaveformCache.set(cacheKey, widget)
        return widget.toEditorWidget()
      })
      waveformWidgetCacheRef.current = nextWaveformCache

      const nextLevelCache = new Map<string, LevelWidget>()
      const levelWidgets = levelInfos.map((info) => {
        const cacheKey = `level-${contextId}-${info.id}-${info.line}-${info.column}`
        let widget = levelWidgetCacheRef.current.get(cacheKey)
        if (!widget) {
          const shouldAnimate = () => {
            const { isPlaying } = usePlaybackStore.getState()
            const { playingTabId } = usePlaybackStore.getState()
            const { activeTabId: currentActiveTabId } = useTabsStore.getState()
            const { channelMode: currentChannelMode } = useChannelModeStore.getState()

            if (!isPlaying) return false

            return currentChannelMode === 'dual'
              ? (playingTabId === null)
              : (playingTabId === currentActiveTabId)
          }

          widget = new LevelWidget(info, baseTheme, shouldAnimate)
          widget.setContextId(contextId)
        }
        else {
          widget.setContextId(contextId)
        }
        nextLevelCache.set(cacheKey, widget)
        return widget.toEditorWidget()
      })
      levelWidgetCacheRef.current = nextLevelCache

      const nextTimelineCache = new Map<string, TimelineWidget>()
      const timelineWidgets = timelineInfos.map((info) => {
        // Include pattern and beat in cache key so widgets update when these change
        const cacheKey =
          `timeline-${contextId}-${info.id}-${info.line}-${info.column}-${info.pattern}-${info.beat}-${info.segmentIndex}`
        let widget = timelineWidgetCacheRef.current.get(cacheKey)
        if (!widget) {
          widget = new TimelineWidget(
            info,
            timelinePositionsRef,
          )
          widget.setContextId(contextId)
        }
        else {
          // Update widget with new info in case pattern/beat/position changed
          widget.setInfo(info)
          widget.setContextId(contextId)
        }
        nextTimelineCache.set(cacheKey, widget)
        return widget.toEditorWidget()
      })
      timelineWidgetCacheRef.current = nextTimelineCache

      if (timelineInfos.length > 0) {
        console.log('[EditorPanel] Created', timelineWidgets.length, 'timeline widgets from', timelineInfos.length,
          'infos')
      }

      if (timelineInfos.length > 0) {
        console.log('[EditorPanel] Creating timeline widgets:', timelineInfos.length, 'widgets from', timelineInfos)
        console.log('[EditorPanel] Timeline widgets created:', timelineWidgets.length)
      }

      const allWidgets = [...numberWidgets, ...patternWidgets, ...amplitudeWidgets, ...waveformWidgets, ...levelWidgets,
        ...timelineWidgets]
      if (timelineInfos.length > 0) {
        console.log('[EditorPanel] Total widgets:', allWidgets.length, 'including', timelineWidgets.length,
          'timeline widgets')
        console.log('[EditorPanel] All widgets:', allWidgets)
        console.log('[EditorPanel] Timeline widgets specifically:', timelineWidgets)
      }
      return allWidgets
    }, [numbersWithParams, patternEvents, amplitudeInfos, waveformInfos, levelInfos, timelineInfos, theme, activeTabId,
      channel])

    // Create a stable reference key for sequences to detect changes
    const sequencesKey = sequences.length > 0
      ? sequences.map(s => `${s.id}-${s.notes.length}`).join(',')
      : ''

    // Create a stable reference key for timelines to detect changes
    // Use unique timeline IDs only (not all segment widgets)
    const uniqueTimelineKeys = new Map<number, string>()
    for (const info of timelineInfos) {
      // Always update to ensure we have the latest pattern and beat
      uniqueTimelineKeys.set(info.id, `${info.id}-${info.pattern}-${info.beat}`)
    }
    const timelinesKey = uniqueTimelineKeys.size > 0
      ? Array.from(uniqueTimelineKeys.values()).join(',')
      : ''

    if (timelineInfos.length > 0) {
      console.log('[EditorPanel] timelinesKey (unique):', timelinesKey)
    }

    // Clean up waveform delay buffers only when waveform IDs actually change
    // Use a stable key to detect actual changes, not just array reference changes
    const amplitudeIdsKey = useMemo(() => {
      return amplitudeInfos.map(w => w.id).sort().join(',')
    }, [amplitudeInfos])

    const waveformIdsKey = useMemo(() => {
      return waveformInfos.map(w => w.id).sort().join(',')
    }, [waveformInfos])

    const levelIdsKey = useMemo(() => {
      return levelInfos.map(w => w.id).sort().join(',')
    }, [levelInfos])

    const prevAmplitudeIdsKeyRef = useRef<string>('')
    const prevWaveformIdsKeyRef = useRef<string>('')
    const prevLevelIdsKeyRef = useRef<string>('')

    useEffect(() => {
      // Only clean up if waveform IDs actually changed (not just array reference)
      if (amplitudeIdsKey !== prevAmplitudeIdsKeyRef.current) {
        const prevIds = prevAmplitudeIdsKeyRef.current ? prevAmplitudeIdsKeyRef.current.split(',').map(Number) : []
        const currentIds = new Set(amplitudeInfos.map(w => w.id))

        // Remove pointers for waveforms that no longer exist
        for (const waveformId of prevIds) {
          if (!currentIds.has(waveformId)) {
            waveformPointersMap.delete(waveformId)
          }
        }

        prevAmplitudeIdsKeyRef.current = amplitudeIdsKey
      }

      if (waveformIdsKey !== prevWaveformIdsKeyRef.current) {
        const prevIds = prevWaveformIdsKeyRef.current ? prevWaveformIdsKeyRef.current.split(',').map(Number) : []
        const currentIds = new Set(waveformInfos.map(w => w.id))

        // Remove pointers for waveforms that no longer exist
        for (const waveformId of prevIds) {
          if (!currentIds.has(waveformId)) {
            waveformPointersMap.delete(waveformId)
          }
        }

        prevWaveformIdsKeyRef.current = waveformIdsKey
      }

      if (levelIdsKey !== prevLevelIdsKeyRef.current) {
        const prevIds = prevLevelIdsKeyRef.current ? prevLevelIdsKeyRef.current.split(',').map(Number) : []
        const currentIds = new Set(levelInfos.map(w => w.id))

        // Remove pointers for waveforms that no longer exist
        for (const waveformId of prevIds) {
          if (!currentIds.has(waveformId)) {
            waveformPointersMap.delete(waveformId)
          }
        }

        prevLevelIdsKeyRef.current = levelIdsKey
      }
    }, [amplitudeIdsKey, amplitudeInfos, waveformIdsKey, waveformInfos, levelIdsKey, levelInfos])

    // Fetch waveform pointers for WASM buffers
    useEffect(() => {
      const fetchWaveformPointers = async () => {
        if (!dspscript.getAudioContext()) return
        try {
          const pointers = await dspscript.getWaveformPointers()
          waveformPointersRef.current.clear()
          waveformPointersMap.clear()
          for (const pointer of pointers) {
            waveformPointersRef.current.set(pointer.waveformId, {
              bufferPtr: pointer.bufferPtr,
              writePosition: pointer.writePosition,
            })
            waveformPointersMap.set(pointer.waveformId, {
              bufferPtr: pointer.bufferPtr,
              writePosition: pointer.writePosition,
            })
          }
        }
        catch (error) {
          console.error('Failed to fetch waveform pointers:', error)
        }
      }

      if (dspscript.getAudioContext()) {
        fetchWaveformPointers()
      }

      // Re-fetch when amplitude infos change
      const interval = setInterval(fetchWaveformPointers, 1000) // Update every second

      return () => clearInterval(interval)
    }, [amplitudeInfos, waveformInfos])

    // Set up SequencerManager and TimelineManager
    useEffect(() => {
      // Initialize timeline manager regardless of playing state (for visualization)
      const audioContext = dspscript.getAudioContext()
      const sampleRate = audioContext?.sampleRate ?? 48000

      // Reuse existing TimelineManager or create new one
      let timelineManager = timelineManagerRef.current
      if (!timelineManager) {
        timelineManager = new TimelineManager(1, sampleRate)
        timelineManagerRef.current = timelineManager
      }

      if (timelineInfos.length > 0) {
        // Create unique timeline configs - only one per unique timeline ID needed
        // (multiple widgets may share the same timeline ID for different segments)
        const uniqueTimelines = new Map<number, typeof timelineInfos[0]>()
        for (const info of timelineInfos) {
          // Always update to ensure we have the latest pattern and beat
          uniqueTimelines.set(info.id, info)
        }
        const timelineConfigs = Array.from(uniqueTimelines.values()).map(info => ({
          id: info.id,
          pattern: info.pattern,
          beat: info.beat,
        }))
        console.log('[EditorPanel] Updating TimelineManager with configs:', timelineConfigs)
        console.log('[EditorPanel] timelinesKey:', timelinesKey)
        timelineManager.setTimelines(timelineConfigs)
      }

      // Reset timeline manager state to ensure clean start
      timelineManager.reset()

      // Initialize timeline manager counter
      const initSampleCounter = dspscript.getGlobalSampleCounter()
      timelineManager.initializeCounter(initSampleCounter)

      if (!actuallyPlaying) {
        activeNotesRef.current.clear()
        sequencerManagerRef.current = null
        patternLengthsRef.current.clear()
        return
      }

      if (sequences.length === 0) {
        return
      }

      // Clear stale notes when creating new manager (e.g. when sequences change)
      activeNotesRef.current.clear()

      // When sequences change (new track loaded), reset waiting state to allow animations to start
      // This ensures animations start immediately when a new track is loaded while already playing
      // Also check if counter is already high - if so, don't wait at all
      const currentCounter = dspscript.getGlobalSampleCounter()
      if (currentCounter >= 1000) {
        // Counter is already high, so we're loading a new track while already playing
        // Don't wait for reset - animations should start immediately
        waitingForCounterResetRef.current = false
      }
      else {
        // Counter is low, might be a fresh start - allow waiting logic to handle it
        // But reset the waiting state so it can be set fresh if needed
        waitingForCounterResetRef.current = false
      }

      // Initialize SequencerManager with sequences from tab
      const manager = new SequencerManager(1, sampleRate) // oversampling factor, sample rate
      manager.setSequences(sequences)
      sequencesRef.current = sequences

      // Only enable sequences if this tab is the one playing
      const isTabPlaying = activeTabId === playingTabId
      if (isTabPlaying) {
        const sequenceIds = new Set(sequences.map(s => s.id))
        manager.setEnabledSequences(sequenceIds)
      }
      else {
        manager.setEnabledSequences(null)
      }

      sequencerManagerRef.current = manager

      // Reset sequencer manager state to ensure clean start
      // This clears any old tracking state that might cause a flash of previous positions
      manager.reset()

      // Get BPM directly from WASM (respects program BPM if set, otherwise global BPM)
      const currentBpm = dspscript.getCurrentBpm()

      // Initialize counter with current sample counter to match current playback position
      // This ensures widgets are synchronized from the start when brought into view
      manager.initializeCounter(initSampleCounter, currentBpm)

      // Sync sequencer states once after loadProgram (when sequencesKey changes)
      const syncSequencerStates = () => {
        const wasmMemory = dspscript.getWasmMemory()
        if (!wasmMemory || !sequencerManagerRef.current) return

        const wasmStates = new Map<number, {
          patternStartSample: number
          currentIndex: number
          shuffleTriggerCount: number
          shuffledIndices: Int32Array | null
          rotatedIndices: Int32Array | null
        }>()

        for (const [cachedSequenceId, statePtr] of sequencerPointersRef.current.entries()) {
          const state = readSequencerStateFromBuffer(wasmMemory, statePtr)
          if (state) {
            const actualSequenceId = state.sequenceId
            wasmStates.set(actualSequenceId, {
              patternStartSample: state.patternStartSample,
              currentIndex: state.currentIndex,
              shuffleTriggerCount: state.shuffleTriggerCount,
              shuffledIndices: state.shuffledIndices,
              rotatedIndices: state.rotatedIndices,
            })
          }
        }

        if (sequencerManagerRef.current) {
          sequencerManagerRef.current.syncFromWasmStates(wasmStates)
        }
      }

      // Try to sync immediately if pointers are already available (e.g. when switching tabs)
      // This ensures widgets are synchronized from the start
      if (sequencerPointersRef.current.size > 0) {
        syncSequencerStates()
      }

      // Request sequencer pointers from worklet via RPC (refresh pointers and sync again)
      dspscript.getSequencerPointers().then(pointers => {
        // Cache pointers for direct memory access
        sequencerPointersRef.current.clear()
        for (const pointer of pointers) {
          // Use sequenceId (from SequenceData.id) as the key, not seqId (sequencer slot ID)
          const sequenceId = pointer.sequenceId
          const statePtr = pointer.statePtr
          sequencerPointersRef.current.set(sequenceId, statePtr)
        }
        // Sync once after pointers are received
        syncSequencerStates()
      }).catch(err => {
        console.error('Failed to get sequencer pointers:', err)
      })

      // Don't initialize pattern starts explicitly - let them calculate naturally on first update
      // This matches AssemblyScript sequencer behavior where pattern start is calculated
      // from globalSampleCounter on first call to fillBlock

      // Pre-calculate pattern lengths and create mapping from expanded noteIndex to original pattern element
      // Pattern events are created from original pattern, but sequence has expanded notes
      // We need to map expanded noteIndex back to the original pattern element index
      const patternLengths = new Map<number, number>()
      const noteIndexToPatternEventMap = new Map<number, Map<number, number>>() // sequenceId -> (noteIndex -> patternEvent noteIndex)

      // Group pattern events by their original pattern element (line, column, length)
      // This allows multiple expanded notes to map to the same original pattern widget
      for (const seq of sequences) {
        // Use cycleLength if available (for nested alternating patterns), otherwise use full length
        const patternLength = (seq as any).cycleLength && (seq as any).cycleLength > 0
          ? (seq as any).cycleLength
          : seq.notes.length
        patternLengths.set(seq.id, patternLength)

        // Get all pattern events for this sequence, sorted by noteIndex
        const seqPatternEvents = patternEvents
          .filter(pe => pe.sequenceId === seq.id && pe.noteIndex !== undefined)
          .sort((a, b) => (a.noteIndex ?? 0) - (b.noteIndex ?? 0))

        if (seqPatternEvents.length === 0) continue

        // Group pattern events by their widget key (line, column, length)
        // This identifies which pattern events came from the same original pattern element
        const widgetGroups = new Map<string, PatternEventInfo[]>()
        for (const pe of seqPatternEvents) {
          const widgetKey = `${pe.line}-${pe.column}-${pe.length}`
          if (!widgetGroups.has(widgetKey)) {
            widgetGroups.set(widgetKey, [])
          }
          widgetGroups.get(widgetKey)!.push(pe)
        }

        // Create mapping: map each expanded noteIndex to the pattern event's noteIndex
        // For notes that don't have a pattern event, map them to the pattern event from the same widget group
        // Pattern events represent the first cycle, so we need to map subsequent cycles back to the first cycle
        const noteIndexMap = new Map<number, number>()
        const originalPatternLength = seqPatternEvents.length
        const expandedSequenceLength = seq.notes.length
        // Use cycleLength if available (for nested alternating patterns), otherwise use originalPatternLength
        const cycleLength = (seq as any).cycleLength && (seq as any).cycleLength > 0
          ? (seq as any).cycleLength
          : originalPatternLength

        // First, map pattern events to themselves
        for (const pe of seqPatternEvents) {
          if (pe.noteIndex !== undefined) {
            noteIndexMap.set(pe.noteIndex, pe.noteIndex)
          }
        }

        // For notes beyond the cycle length, map them back to the corresponding pattern event
        // This handles multipliers, replication, and nested alternating patterns
        for (let expandedNoteIndex = cycleLength; expandedNoteIndex < expandedSequenceLength; expandedNoteIndex++) {
          // Map to the corresponding pattern event in the first cycle
          // Use cycleLength for wrapping, not originalPatternLength
          const patternEventIndex = expandedNoteIndex % cycleLength
          const patternEvent = seqPatternEvents[patternEventIndex]
          if (patternEvent?.noteIndex !== undefined) {
            noteIndexMap.set(expandedNoteIndex, patternEvent.noteIndex)
          }
        }

        noteIndexToPatternEventMap.set(seq.id, noteIndexMap)
      }
      patternLengthsRef.current = patternLengths // Store the mapping for use in active note updates
      ;(patternLengthsRef as any).noteIndexToPatternEventMap = noteIndexToPatternEventMap

      return () => {
        activeNotesRef.current.clear()
        // Restore original message handler if it exists
        const workletNode = dspscript.getWorkletNode()
        if (workletNode) {
          // Note: We can't easily restore the original handler here since we don't store it
          // The handler will be replaced on next effect run anyway
        }
      }
    }, [actuallyPlaying, sequencesKey, timelinesKey, channel, activeTabId, playingTabId, channelMode,
      patternEvents.length, sequences, timelineInfos])

    // Callback to update sequencer state and waveforms before each draw
    const handleBeforeDraw = useCallback(() => {
      // Check if audio is actually playing
      if (!dspscript.isPlaying()) {
        return
      }

      if (!sequencerManagerRef.current) {
        return
      }

      if (sequencesRef.current.length === 0) {
        return
      }

      // Update enabled sequences based on whether this tab is playing
      const currentActiveTabId = useTabsStore.getState().activeTabId
      const currentPlayingTabId = usePlaybackStore.getState().playingTabId
      const currentChannelMode = useChannelModeStore.getState().channelMode
      // Get BPM directly from WASM (respects program BPM if set, otherwise global BPM)
      const currentBpm = dspscript.getCurrentBpm()

      // In dual mode, playingTabId is null, so both panels should be enabled.
      // In single mode, only enable if this tab is the playing tab.
      // If in single mode and playingTabId is null, it means non-editor playback (e.g. TrackList),
      // so don't animate editor sequences.
      const isTabPlaying = currentChannelMode === 'dual'
        ? (currentPlayingTabId === null)
        : (currentPlayingTabId === currentActiveTabId)

      if (!isTabPlaying) {
        if (sequencerManagerRef.current) {
          sequencerManagerRef.current.setEnabledSequences(null)
        }
        // Clear active notes when tab stops being the playing tab
        activeNotesRef.current.clear()
        timelinePositionsRef.current.clear()
        return
      }

      if (sequencerManagerRef.current) {
        const sequenceIds = new Set(sequencesRef.current.map(s => s.id))
        sequencerManagerRef.current.setEnabledSequences(sequenceIds)
      }

      try {
        const globalSampleCounter = dspscript.getGlobalSampleCounter()

        // If we're waiting for counter reset, check if it has reset to a low value
        // Also check if counter is increasing (playback has started) or timeout has passed
        if (waitingForCounterResetRef.current) {
          const timeWaiting = Date.now() - waitingStartTimeRef.current
          const counterIncreased = lastCounterWhenWaitingRef.current >= 0
            && globalSampleCounter > lastCounterWhenWaitingRef.current
          const timeoutPassed = timeWaiting > 500 // 500ms timeout

          if (globalSampleCounter < 1000 || counterIncreased || timeoutPassed) {
            // Counter has reset, is increasing (playback started), or timeout passed
            // Clear state and allow updates
            activeNotesRef.current.clear()
            timelinePositionsRef.current.clear()
            if (sequencerManagerRef.current) {
              // Reset manager and all sequencers to ensure clean start
              sequencerManagerRef.current.reset()
              sequencerManagerRef.current.initializeCounter(globalSampleCounter, currentBpm)
            }
            if (timelineManagerRef.current) {
              timelineManagerRef.current.reset()
              timelineManagerRef.current.initializeCounter(globalSampleCounter)
            }
            waitingForCounterResetRef.current = false
            currentSampleCounterRef.current = globalSampleCounter
          }
          else {
            // Counter hasn't reset yet and isn't increasing, skip this update
            lastCounterWhenWaitingRef.current = globalSampleCounter
            return
          }
        }

        // Sync sequencer states from WASM before update (but only after counter reset is confirmed)
        // This ensures we don't use stale WASM state that might have old patternStartSample values
        const wasmMemory = dspscript.getWasmMemory()
        if (wasmMemory && sequencerManagerRef.current) {
          const wasmStates = new Map<number, {
            patternStartSample: number
            currentIndex: number
            shuffleTriggerCount: number
            shuffledIndices: Int32Array | null
            rotatedIndices: Int32Array | null
          }>()

          for (const [cachedSequenceId, statePtr] of sequencerPointersRef.current.entries()) {
            const state = readSequencerStateFromBuffer(wasmMemory, statePtr)
            if (state) {
              const actualSequenceId = state.sequenceId
              wasmStates.set(actualSequenceId, {
                patternStartSample: state.patternStartSample,
                currentIndex: state.currentIndex,
                shuffleTriggerCount: state.shuffleTriggerCount,
                shuffledIndices: state.shuffledIndices,
                rotatedIndices: state.rotatedIndices,
              })
            }
          }

          if (sequencerManagerRef.current) {
            sequencerManagerRef.current.syncFromWasmStates(wasmStates)
          }
        }
        else {
          // If counter reset (went backwards significantly), clear state and reinitialize
          if (currentSampleCounterRef.current > 1000 && globalSampleCounter < 100) {
            activeNotesRef.current.clear()
            timelinePositionsRef.current.clear()
            if (sequencerManagerRef.current) {
              sequencerManagerRef.current.reset()
              sequencerManagerRef.current.initializeCounter(globalSampleCounter, currentBpm)
            }
            if (timelineManagerRef.current) {
              timelineManagerRef.current.reset()
              timelineManagerRef.current.initializeCounter(globalSampleCounter)
            }
          }
          currentSampleCounterRef.current = globalSampleCounter
        }

        // IMPORTANT: Sync happens BEFORE update, so getPosition will use synced patternStartSample
        const noteEvents = sequencerManagerRef.current.update(globalSampleCounter, currentBpm)

        // Update timeline positions
        if (timelineManagerRef.current) {
          const timelinePositions = timelineManagerRef.current.getAllPositions(globalSampleCounter, currentBpm)
          timelinePositionsRef.current.clear()
          for (const [id, position] of timelinePositions) {
            timelinePositionsRef.current.set(id, position)
          }
        }

        // Update active notes - track all notes that play between animation frames
        // When new notes play in a sequence, they replace previous active notes for that sequence
        // Notes stay active until replaced by new notes (not based on fade duration)
        const patternLengths = patternLengthsRef.current
        const sequencesWithNewNotes = new Set<number>()

        // First, collect all new notes and mark which sequences have new notes
        // Map expanded noteIndex to pattern event noteIndex so multiple expanded notes highlight the same widget
        const noteIndexToPatternEventMap = (patternLengthsRef as any).noteIndexToPatternEventMap as Map<number,
          Map<number, number>> | undefined
        for (const event of noteEvents) {
          const patternLength = patternLengths.get(event.sequenceId)
          // Map expanded noteIndex to pattern event noteIndex
          let patternEventNoteIndex = event.noteIndex
          if (noteIndexToPatternEventMap) {
            const seqMap = noteIndexToPatternEventMap.get(event.sequenceId)
            if (seqMap) {
              const mapped = seqMap.get(event.noteIndex)
              if (mapped !== undefined) {
                patternEventNoteIndex = mapped
              }
            }
          }
          const activeKey = `${event.sequenceId}-${patternEventNoteIndex}`
          // Add all notes that play between frames to active set
          activeNotesRef.current.set(activeKey, event.samplePosition)
          sequencesWithNewNotes.add(event.sequenceId)
        }

        // Clear old notes for sequences that have new notes (new notes replace old ones)
        // This ensures only the most recent notes are active
        for (const sequenceId of sequencesWithNewNotes) {
          const newNoteKeys = new Set<string>()
          const seqMap = noteIndexToPatternEventMap?.get(sequenceId)
          for (const event of noteEvents) {
            if (event.sequenceId === sequenceId) {
              // Map expanded noteIndex to pattern event noteIndex
              let patternEventNoteIndex = event.noteIndex
              if (seqMap) {
                const mapped = seqMap.get(event.noteIndex)
                if (mapped !== undefined) {
                  patternEventNoteIndex = mapped
                }
              }
              newNoteKeys.add(`${sequenceId}-${patternEventNoteIndex}`)
            }
          }
          // Remove all old notes for this sequence that aren't in the new set
          for (const key of activeNotesRef.current.keys()) {
            if (key.startsWith(`${sequenceId}-`) && !newNoteKeys.has(key)) {
              activeNotesRef.current.delete(key)
            }
          }
        }

        // Clean up active notes for sequences that no longer exist
        const currentSequenceIds = new Set(sequencesRef.current.map(s => s.id))
        for (const key of activeNotesRef.current.keys()) {
          const sequenceId = parseInt(key.split('-')[0])
          if (!currentSequenceIds.has(sequenceId)) {
            activeNotesRef.current.delete(key)
          }
        }
      }
      catch (error) {
        console.error('[EditorPanel] Error in beforeDraw callback:', error)
      }
    }, [])

    return (
      <div
        ref={containerRef}
        className="flex-1 overflow-auto min-h-0 relative"
      >
        {showFlash && (
          <div
            className="absolute inset-0 bg-white pointer-events-none z-[9999]"
            style={{ mixBlendMode: 'difference' }}
          />
        )}
        <CodeEditor
          wordWrap={wordWrap}
          gutter
          codeFile={codeFile}
          theme={theme}
          tokenizer={tokenizer}
          functionDefinitions={functionDefinitions}
          errors={errors}
          keyOverride={handleKeyOverride}
          hideFunctionSignatures={hideFunctionSignatures}
          onPointerDown={handlePointerDown}
          widgets={widgets}
          isAnimating={actuallyPlaying
            && (channelMode === 'dual' ? playingTabId === null : playingTabId === activeTabId)}
          onBeforeDraw={handleBeforeDraw}
        />
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Only rerender if codeFile reference changes, fullscreen state changes, or keyOverride changes
    return (
      prevProps.codeFile === nextProps.codeFile
      && prevProps.channel === nextProps.channel
      && prevProps.isFullscreen === nextProps.isFullscreen
      && prevProps.keyOverride === nextProps.keyOverride
    )
  },
)

EditorPanel.displayName = 'EditorPanel'
